import { canAccessService, hasAllPermissions, type ServiceCode } from "@app/api-client";
import type { ReactNode } from "react";
import { Outlet } from "react-router";

import { ForbiddenPage } from "@/pages/ForbiddenPage";

import { useAuth } from "./authContext";

interface RequirePermissionProps {
  /** 必須能進入這個服務 */
  service?: ServiceCode;
  /** 必須具備這些權限，例如 ["master.view_plant"] */
  permissions?: string[];
  /**
   * 要保護的內容。
   *
   * 省略時渲染 `<Outlet />`，也就是當 layout route 用（守衛整個子樹）。
   * 給了 children 就只包住那一個元件 —— index route 不能有 children，
   * 所以「服務首頁也要有權限要求」只能靠這一條路。
   */
  children?: ReactNode;
}

/**
 * 路由層級的權限守衛。
 *
 * **這只是 UX。** 真正的授權由後端的 ServiceModelPermissions 執行——
 * 使用者就算手動改 URL 進來，API 一樣會回 403。這裡的作用是讓他看到
 * 一個清楚的「沒有權限」頁面，而不是一堆失敗的請求。
 */
export function RequirePermission({ service, permissions, children }: RequirePermissionProps) {
  const { me } = useAuth();

  if (service && !canAccessService(me, service)) {
    return <ForbiddenPage reason={`您沒有「${service}」服務的使用權限。`} />;
  }

  if (permissions && !hasAllPermissions(me, permissions)) {
    return <ForbiddenPage reason="您沒有執行此操作的權限。" />;
  }

  return <>{children ?? <Outlet />}</>;
}
